/**
 * Side-by-Side-State + „Vergleiche mit"-Zielauflösung (C2-B).
 *
 * Hält Toggle + gewähltes Vergleichsziel, baut die Ziel-Liste (Master + andere
 * Channels) und löst pro Feldtyp den effektiven Zielwert + Quell-Badge auf.
 */

import { useEffect, useMemo, useState } from 'react'
import { mergeFields, type MergedField } from './contentInheritance'
import { readLangText } from './contentBlockLang'
import type { CompareTarget } from './CompareTargetBar'
import type { CompareValue } from './ChannelFieldsList'
import type { BlockType, ContentBlock } from '@/types/content-blocks'
import type { ChannelPackage } from '@/types/channel-packages'

interface Params {
  isProductScope: boolean
  packages: ChannelPackage[]
  selectedChannelId: string
  masterBlocks: ContentBlock[]
  channelBlocksByPkg: Record<string, ContentBlock[]>
  selectedLang: string
  masterName: string
}

export function useSideBySide(p: Params) {
  const [sideBySide, setSideBySide] = useState(false)
  const [compareTargetId, setCompareTargetId] = useState('')

  const compareTargets: CompareTarget[] = useMemo(
    () => [
      ...(p.isProductScope ? [] : [{ id: 'product', name: p.masterName, isMaster: true }]),
      ...p.packages.filter((pkg) => pkg.id !== p.selectedChannelId).map((pkg) => ({ id: pkg.id, name: pkg.channel?.name ?? pkg.id })),
    ],
    [p.isProductScope, p.packages, p.selectedChannelId, p.masterName],
  )

  useEffect(() => {
    if (sideBySide && !compareTargets.some((c) => c.id === compareTargetId)) {
      setCompareTargetId(compareTargets[0]?.id ?? '')
    }
  }, [sideBySide, compareTargets, compareTargetId])

  const targetByType = useMemo(() => {
    const list: MergedField[] =
      compareTargetId === 'product'
        ? p.masterBlocks.map((b) => ({ blockType: b.block_type, block: b, inherited: false }))
        : compareTargetId
          ? mergeFields(p.masterBlocks, p.channelBlocksByPkg[compareTargetId] ?? [])
          : []
    return new Map(list.map((f) => [f.blockType, f]))
  }, [compareTargetId, p.masterBlocks, p.channelBlocksByPkg])

  const targetName =
    compareTargetId === 'product'
      ? p.masterName
      : (p.packages.find((pkg) => pkg.id === compareTargetId)?.channel?.name ?? compareTargetId)

  const resolveTarget = (blockType: BlockType): CompareValue => {
    const entry = targetByType.get(blockType)
    return {
      name: targetName,
      value: readLangText(entry?.block.current_version?.content, p.selectedLang),
      badge: compareTargetId === 'product' ? 'master' : entry?.inherited ? 'inherited' : 'override',
    }
  }

  return { sideBySide, setSideBySide, compareTargetId, setCompareTargetId, compareTargets, resolveTarget }
}
